import React from 'react';
import { Menu, Sun, Moon, ChevronDown, Search, Settings, LogOut } from 'lucide-react';

/**
 * Komponen Header untuk navigasi atas
 * @param {boolean} isDarkMode - Status dark mode
 * @param {function} toggleTheme - Callback untuk toggle theme
 * @param {string} activeMenu - ID menu yang aktif
 * @param {boolean} userMenuOpen - Status user menu dropdown
 * @param {function} setUserMenuOpen - Callback untuk set user menu
 * @param {function} onToggleSidebar - Callback untuk toggle sidebar
 * @param {function} onLogout - Callback untuk logout
 */
const Header = ({
    isDarkMode,
    toggleTheme,
    activeMenu,
    userMenuOpen,
    setUserMenuOpen,
    onToggleSidebar,
    onLogout
}) => {
    /**
     * Format judul halaman dari ID menu
     */
    const formatPageTitle = (menuId) => {
        return menuId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <header className={`
            h-14 border-b flex items-center justify-between px-4 sticky top-0 z-10 
            ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}
        `}>
            {/* Left Section */}
            <div className="flex items-center gap-3">
                {/* Sidebar Toggle (Desktop) */}
                <button
                    onClick={onToggleSidebar}
                    className={`hidden md:block p-1.5 rounded text-slate-500 ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                >
                    <Menu size={18} />
                </button>

                {/* Logo (Mobile) */}
                <div className="md:hidden flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center text-white font-bold text-xs">
                        C
                    </div>
                    <span className="font-bold text-base">Cloudnine</span>
                </div>

                {/* Page Title (Desktop) */}
                <h1 className="text-sm font-semibold hidden md:block">
                    {formatPageTitle(activeMenu)}
                </h1>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3 md:gap-4">
                {/* Search Input */}
                <div className="relative hidden sm:block">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search..."
                        className={`
                            h-8 pl-8 pr-3 rounded text-xs focus:outline-none border 
                            ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-slate-50 border-slate-200'}
                        `}
                    />
                </div>

                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-yellow-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                    {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                </button>

                {/* User Menu */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className={`flex items-center gap-2 p-1.5 rounded transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                    >
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-200">
                            AD
                        </div>
                        <span className="text-xs font-medium hidden sm:block">Admin User</span>
                        <ChevronDown size={12} className="text-slate-400" />
                    </button>

                    {/* User Dropdown */}
                    {userMenuOpen && (
                        <div className={`
                            absolute right-0 mt-2 w-40 rounded shadow-lg border py-1 
                            animate-in fade-in zoom-in-95 duration-200 z-50 
                            ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                        `}>
                            {/* User Info */}
                            <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <p className="text-xs font-semibold">Admin User</p>
                                <p className="text-[10px] text-slate-500">admin@cloudnine.com</p>
                            </div>

                            {/* Settings */}
                            <button className={`w-full text-left px-4 py-2 text-xs flex items-center gap-2 ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}>
                                <Settings size={12} /> Settings
                            </button>

                            {/* Logout */}
                            <button
                                onClick={onLogout}
                                className={`w-full text-left px-4 py-2 text-xs flex items-center gap-2 text-rose-500 ${isDarkMode ? 'hover:bg-rose-900/20' : 'hover:bg-rose-50'}`}
                            >
                                <LogOut size={12} /> Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
